// src/pages/DocWriterStartPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import DocSubjectSelect from "../components/DocSubjectSelect";
import DocToneSelect from "../components/DocToneSelect";
import DocAudienceSelect from "../components/DocAudienceSelect";
import DocLanguageSelect from "../components/DocLanguageSelect";
import DocDynamicFieldsForm from "../components/DocDynamicFieldsForm";
import DocParticipantsForm from "../components/DocParticipantsForm";
import DocSourcesPanel from "../components/DocSourcesPanel";
import DocGenerateDraftBar from "../components/DocGenerateDraftBar";

import { parseContactBlock } from "../utils/docAddressParser";
import { getAuth } from "firebase/auth";
import { launchDocwriter } from "../utils/docApiClient";
import { EMAIL_RX, PHONE_RX } from "../utils/docRegexUtils";
import { isIOSApp } from "../utils/platform";

const HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const TERMS_URL = process.env.REACT_APP_TERMS_URL || "/terms";
const PRIVACY_URL =
  process.env.REACT_APP_PRIVACY_URL || "/privacy-policy";

const DOCWRITER_MAX_SOURCE_FILES = 5;
const DOCWRITER_MAX_PAGES_PER_FILE = 25;
const DOCWRITER_MAX_TOTAL_SOURCE_PAGES = 50;

const MONTHLY_PLAN_TYPES = new Set([
  "monthly",
  "monthly_essential",
  "monthly_pro",
  "monthly_elite",
  "monthly_unlimited",
]);

const MONTHLY_PLAN_LABELS = {
  monthly: "Monthly plan",
  monthly_essential: "Monthly Essential",
  monthly_pro: "Monthly Pro",
  monthly_elite: "Monthly Elite",
  monthly_unlimited: "Monthly Unlimited",
};

function useIsSmallScreen(breakpoint = 1024) {
  const getInitial = () =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false;

  const [isSmall, setIsSmall] = useState(getInitial());

  useEffect(() => {
    const onResize = () => setIsSmall(getInitial());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return isSmall;
}

function parseDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function activeByDateAndFlag(data = {}) {
  if (data.is_active === false || data.isActive === false) return false;

  const candidates = [
    data.current_period_end,
    data.currentPeriodEnd,
    data.subscription_current_period_end,
    data.subscriptionCurrentPeriodEnd,
    data.expires_at,
    data.expiresAt,
  ];

  for (const value of candidates) {
    if (!value) continue;
    const date = parseDate(value);
    if (!date) continue;
    return date.getTime() > Date.now();
  }

  return true;
}

function monthlyPlanIsActive(data = {}) {
  const planType = String(data.plan_type || data.planType || "")
    .trim()
    .toLowerCase();
  return MONTHLY_PLAN_TYPES.has(planType) && activeByDateAndFlag(data);
}

function trialPlanIsActive(data = {}) {
  const planType = String(data.plan_type || data.planType || "")
    .trim()
    .toLowerCase();
  return planType === "trial_7d" && activeByDateAndFlag(data);
}

function readEntitlementNumber(bucket, ...keys) {
  if (!bucket || typeof bucket !== "object") {
    return { found: false, value: 0 };
  }

  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(bucket, key)) continue;
    const n = Number(bucket[key]);
    if (Number.isFinite(n)) {
      return { found: true, value: Math.max(0, Math.trunc(n)) };
    }
  }

  return { found: false, value: 0 };
}

function formatBytes(bytes) {
  const n = Math.max(0, Number(bytes || 0));
  if (n >= 1024 * 1024) {
    return `${(n / (1024 * 1024)).toFixed(n >= 10 * 1024 * 1024 ? 1 : 2)} MB`;
  }
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

function formatDuration(milliseconds) {
  const totalSeconds = Math.max(0, Math.floor(Number(milliseconds || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

function formatUploadSpeed(bytesPerSecond) {
  const value = Math.max(0, Number(bytesPerSecond || 0));
  if (!value) return "Calculating speed…";
  return `${formatBytes(value)}/s`;
}

function positiveWholePageCount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  return Math.max(1, Math.trunc(numeric));
}

function sourcePageCount(item = {}) {
  const candidates = [
    item.pages,
    item.pageCount,
    item.declaredPages,
    item.declaredPageCount,
    item.userPages,
    item.userPageCount,
    item.detectedPages,
    item.detectedPageCount,
    item.pdfPageCount,
    item.actualPageCount,
    item?.metadata?.pages,
    item?.metadata?.pageCount,
    item?.metadata?.declaredPages,
    item?.metadata?.declaredPageCount,
  ];

  for (const candidate of candidates) {
    const pages = positiveWholePageCount(candidate);
    if (pages > 0) return pages;
  }

  return 0;
}

function sourceDisplayName(item = {}) {
  return String(
    item.name ||
      item.filename ||
      item.rawFile?.name ||
      item.file?.name ||
      "Supporting file"
  ).trim();
}

function docWriterLaunchPhaseMessage(event = {}) {
  const fileNumber = Number(event.fileNumber || event.fileIndex || 0);
  const totalFiles = Number(event.totalFiles || 0);
  const fileName = String(event.fileName || "").trim();
  const fileLabel =
    fileNumber && totalFiles
      ? `file ${fileNumber.toLocaleString()} of ${totalFiles.toLocaleString()}`
      : "your supporting files";
  const namedFile = fileName ? `: ${fileName}` : "";

  if (event.type === "workspace_start" || event.phase === "creating_job") {
    return "Creating your Doc Writer workspace…";
  }
  if (event.type === "job_created") {
    return totalFiles > 0
      ? `Workspace created. Preparing ${totalFiles.toLocaleString()} supporting file${
          totalFiles === 1 ? "" : "s"
        }…`
      : "Workspace created. Starting your document…";
  }
  if (event.type === "no_sources") {
    return "No supporting files selected. Starting your document…";
  }
  if (event.type === "batch_start") {
    return `Preparing ${totalFiles.toLocaleString()} supporting file${
      totalFiles === 1 ? "" : "s"
    }…`;
  }
  if (event.phase === "requesting_upload_url") {
    return `Preparing ${fileLabel}${namedFile}`;
  }
  if (event.phase === "uploading_file" || event.type === "upload_progress") {
    return `Uploading ${fileLabel}${namedFile}`;
  }
  if (event.phase === "confirming_upload") {
    return `Confirming ${fileLabel}${namedFile}`;
  }
  if (event.type === "file_complete") {
    return `Uploaded ${fileLabel}${namedFile}`;
  }
  if (event.type === "batch_complete") {
    return "All supporting files uploaded successfully.";
  }
  if (
    event.type === "generation_start" ||
    event.phase === "queueing_generation"
  ) {
    return "Upload complete. Starting your document…";
  }
  if (event.type === "queued" || event.phase === "queued") {
    return "Document queued. Opening your editor…";
  }
  return "Preparing your Doc Writer workspace…";
}

function prettyLanguage(value) {
  const raw = String(value || "auto").trim();
  if (!raw || raw === "auto") return "Auto-detect";
  return raw
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function prettyValue(value, fallback = "Not selected") {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  return raw
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function normalizeParticipantRole(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function normalizeParticipantForPayload(participant = {}) {
  const entityType =
    participant?.entityType === "organization" ? "organization" : "person";

  return {
    id: String(participant?.id || "").trim(),
    entityType,
    name: String(participant?.name || "").trim(),
    role: String(participant?.role || "").trim(),
    title: String(participant?.title || "").trim(),
    organizationName: String(participant?.organizationName || "").trim(),
    representativeName: String(participant?.representativeName || "").trim(),
    contactBlock: String(participant?.contactBlock || "").trim(),
    details: String(participant?.details || "").trim(),
  };
}

function participantContactBlock(participant = {}) {
  const normalized = normalizeParticipantForPayload(participant);
  const lines = [];

  if (normalized.name) lines.push(normalized.name);

  if (normalized.entityType === "organization") {
    if (normalized.representativeName) {
      lines.push(`Attn: ${normalized.representativeName}`);
    }
  } else {
    if (normalized.title) lines.push(normalized.title);
    if (normalized.organizationName) lines.push(normalized.organizationName);
  }

  if (normalized.contactBlock) lines.push(normalized.contactBlock);

  return lines.filter(Boolean).join("\n").trim();
}

function participantToLegacyContact(participant) {
  if (!participant) return parseContactBlock("");

  const normalized = normalizeParticipantForPayload(participant);
  const raw = participantContactBlock(normalized);
  const parsed = parseContactBlock(raw);

  return {
    ...parsed,
    raw,
    role: normalized.role,
    entityType: normalized.entityType,
    title: normalized.title,
    organizationName: normalized.organizationName,
    representativeName: normalized.representativeName,
    details: normalized.details,
  };
}

function findParticipantByRole(participants, roleName) {
  const target = normalizeParticipantRole(roleName);

  return (Array.isArray(participants) ? participants : []).find((participant) =>
    normalizeParticipantRole(participant?.role).includes(target)
  );
}

const DocWriterStartPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isLoggedIn = !!currentUser;
  const isSmall = useIsSmallScreen();
  const onIOSApp = isIOSApp();

  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [audience, setAudience] = useState("");
  const [tone, setTone] = useState("formal");
  const [readingLevel, setReadingLevel] = useState("standard");
  const [language, setLanguage] = useState("auto");

  const [participants, setParticipants] = useState([]);
  const [participantsValid, setParticipantsValid] = useState(true);
  const [fieldValues, setFieldValues] = useState({});
  const [formValid, setFormValid] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const launchProtectionRef = useRef(false);
  const launchUiThrottleRef = useRef(0);
  const [launchPhase, setLaunchPhase] = useState("");
  const [launchProgress, setLaunchProgress] = useState({
    type: "idle",
    phase: "idle",
    fileNumber: 0,
    totalFiles: 0,
    fileName: "",
    sizeBytes: 0,
    loadedBytes: 0,
    totalBytes: 0,
    percent: 0,
    elapsedMs: 0,
    uploadedCount: 0,
    averageBytesPerSecond: 0,
    fileStartedAt: "",
  });
  const [launchClockTick, setLaunchClockTick] = useState(0);
  const [sourceItems, setSourceItems] = useState([]);
  const [sourceUploadNotice, setSourceUploadNotice] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [billingNotice, setBillingNotice] = useState(null);
  const [voiceBillingDialogOpen, setVoiceBillingDialogOpen] = useState(false);

  const [billingSnapshot, setBillingSnapshot] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [billingLoaded, setBillingLoaded] = useState(false);

  const fetchBillingSnapshot = async () => {
    if (!currentUser) {
      setBillingSnapshot(null);
      setBillingError("");
      setBillingLoaded(false);
      return null;
    }

    setBillingLoading(true);
    setBillingError("");

    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`${HOST}/user/billing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${response.status}`);
      }

      setBillingSnapshot(data || {});
      setBillingLoaded(true);
      setBillingNotice(null);
      return data || {};
    } catch (error) {
      console.error("[DocStart] Billing fetch error:", error);
      setBillingSnapshot(null);
      setBillingLoaded(false);
      setBillingError(
        "Unable to load your Doc Writer access right now. You may refresh this card or continue and let the server verify access."
      );
      return null;
    } finally {
      setBillingLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) {
      setBillingSnapshot(null);
      setBillingError("");
      setBillingLoaded(false);
      return;
    }

    fetchBillingSnapshot();
    
  }, [currentUser]);


  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!launchProtectionRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (!isGenerating) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isGenerating]);

  useEffect(() => {
    if (!isGenerating || !launchProgress.fileStartedAt) return undefined;
    setLaunchClockTick(Date.now());
    const timer = window.setInterval(() => setLaunchClockTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isGenerating, launchProgress.fileStartedAt]);

  const billingAccess = useMemo(() => {
    const outer = billingSnapshot || {};
    const billing =
      outer.billing && typeof outer.billing === "object" ? outer.billing : outer;

    const planType = String(billing.plan_type || billing.planType || "free")
      .trim()
      .toLowerCase();
    const monthlyActive = monthlyPlanIsActive(billing);
    const trialActive = trialPlanIsActive(billing);

    const monthlyEntitlements =
      billing.monthly_entitlements || billing.monthlyEntitlements || {};
    const addonEntitlements =
      billing.addon_entitlements ||
      billing.addonEntitlements ||
      billing.purchased_addons ||
      {};

    const directRemainingRaw = Number(
      billing.doc_writer_remaining_docs ?? billing.docWriterRemainingDocs
    );
    const directRemaining = Number.isFinite(directRemainingRaw)
      ? Math.max(0, Math.trunc(directRemainingRaw))
      : 0;

    const monthlyRead = readEntitlementNumber(
      monthlyEntitlements,
      "doc_writer_remaining_docs",
      "doc_writer",
      "docWriter"
    );
    const addonRead = readEntitlementNumber(
      addonEntitlements,
      "doc_writer_remaining_docs",
      "doc_writer",
      "docWriter"
    );

    const unlimited = Boolean(
      billing.doc_writer_unlimited ??
        billing.docWriterUnlimited ??
        monthlyEntitlements.doc_writer_unlimited ??
        monthlyEntitlements.docWriterUnlimited
    );

    const monthlyRemaining = monthlyRead.found
      ? monthlyRead.value
      : monthlyActive
      ? directRemaining
      : 0;

    const addonRemaining = addonRead.found
      ? addonRead.value
      : !monthlyActive && !trialActive
      ? directRemaining
      : 0;

    const trialRemaining = trialActive ? directRemaining : 0;

    let source = "none";
    let sourceLabel = "No available access";
    let available = 0;
    let canGenerate = false;

    // Doc Writer access is valid when ANY usable billing bucket is available.
    // Priority is monthly/trial first, then purchased add-on fallback.
    //
    // Examples:
    // - active monthly + monthly quota > 0 -> allow (monthly)
    // - active monthly + monthly quota = 0 + add-on > 0 -> allow (add-on)
    // - expired/inactive monthly + add-on > 0 -> allow (add-on)
    // - no usable monthly/trial/add-on quota -> block
    if (monthlyActive && unlimited) {
      source = "unlimited";
      sourceLabel = "Unlimited monthly plan";
      available = Number.POSITIVE_INFINITY;
      canGenerate = true;
    } else {
      const monthlyUsable = monthlyActive ? monthlyRemaining : 0;
      const trialUsable = trialActive ? trialRemaining : 0;
      const addonUsable = addonRemaining;

      available = monthlyUsable + trialUsable + addonUsable;
      canGenerate = available > 0;

      if (monthlyUsable > 0) {
        source = "monthly";
        sourceLabel = "Monthly plan";
      } else if (trialUsable > 0) {
        source = "trial";
        sourceLabel = "Trial allowance";
      } else if (addonUsable > 0) {
        source = "addon";
        sourceLabel = "Purchased add-on";
      }
    }

    const expiry =
      billing.current_period_end ||
      billing.currentPeriodEnd ||
      billing.expires_at ||
      billing.expiresAt ||
      null;

    return {
      planType,
      planLabel:
        planType === "trial_7d"
          ? "7-Day Trial"
          : MONTHLY_PLAN_LABELS[planType] || prettyValue(planType, "Free plan"),
      monthlyActive,
      trialActive,
      unlimited: monthlyActive && unlimited,
      monthlyRemaining,
      addonRemaining,
      trialRemaining,
      source,
      sourceLabel,
      available,
      canGenerate,
      expiry,
    };
  }, [billingSnapshot]);

  const billingBlocked = Boolean(
    isLoggedIn && billingLoaded && !billingError && !billingAccess.canGenerate
  );

  // Voice input uses the same Doc Writer entitlement decision as Generate.
  // Any usable monthly/trial/add-on Doc Writer entitlement allows voice input.
  // Until access is positively verified, the microphone may be visible but
  // recording/STT is not allowed.
  const voiceAccessAllowed = Boolean(
    isLoggedIn &&
      billingLoaded &&
      !billingLoading &&
      !billingError &&
      billingAccess.canGenerate
  );

  const voiceAccessLoading = Boolean(
    isLoggedIn && (billingLoading || !billingLoaded || !!billingError)
  );

  const handleVoiceBlocked = (reason) => {
    if (reason === "login_required") {
      navigate("/login", { state: { from: "/docwriter/start" } });
      return;
    }

    // Billing is already loaded by the Start Page. While that existing check is
    // still pending (or could not be verified), keep voice quiet and fail closed.
    // Do not show a browser alert and do not make a second billing request.
    if (reason === "billing_loading" || billingError) {
      return;
    }

    if (reason === "billing_required") {
      setVoiceBillingDialogOpen(true);
    }
  };

  useEffect(() => {
    if (!isLoggedIn || voiceAccessAllowed) {
      setVoiceBillingDialogOpen(false);
    }
  }, [isLoggedIn, voiceAccessAllowed]);

  const attachmentSummary = useMemo(() => {
    const selected = Array.isArray(sourceItems) ? sourceItems : [];
    const bytes = selected.reduce(
      (sum, item) => sum + Math.max(0, Number(item?.size || 0)),
      0
    );

    return {
      count: selected.length,
      bytes,
      label: selected.length
        ? `${selected.length} file${selected.length === 1 ? "" : "s"} (${formatBytes(bytes)})`
        : "No supporting files",
    };
  }, [sourceItems]);

  const sourcePolicy = useMemo(() => {
    const selected = Array.isArray(sourceItems) ? sourceItems : [];
    const pageRows = selected.map((item) => ({
      item,
      pages: sourcePageCount(item),
    }));
    const totalPages = pageRows.reduce((sum, row) => sum + row.pages, 0);

    if (selected.length > DOCWRITER_MAX_SOURCE_FILES) {
      return {
        blocked: true,
        reason: "too_many_files",
        totalPages,
        message: `Doc Writer supports up to ${DOCWRITER_MAX_SOURCE_FILES} supporting files. Please remove the extra file before generating your document.`,
      };
    }

    const oversizedFile = pageRows.find(
      (row) => row.pages > DOCWRITER_MAX_PAGES_PER_FILE
    );

    if (oversizedFile) {
      const name = sourceDisplayName(oversizedFile.item);
      return {
        blocked: true,
        reason: "file_page_limit",
        totalPages,
        message: `“${name}” has ${oversizedFile.pages} pages. Doc Writer supports up to ${DOCWRITER_MAX_PAGES_PER_FILE} pages per supporting file. Please remove it or replace it with a smaller file before generating your document.`,
      };
    }

    if (totalPages > DOCWRITER_MAX_TOTAL_SOURCE_PAGES) {
      return {
        blocked: true,
        reason: "combined_page_limit",
        totalPages,
        message: `Your supporting files contain ${totalPages} pages in total. Doc Writer supports up to ${DOCWRITER_MAX_TOTAL_SOURCE_PAGES} pages per document request. Please remove or reduce one or more files before generating your document.`,
      };
    }

    const sourceWithError = selected.find(
      (item) => String(item?.status || "").trim().toLowerCase() === "error"
    );

    if (sourceWithError) {
      const name = sourceDisplayName(sourceWithError);
      return {
        blocked: true,
        reason: "source_error",
        totalPages,
        message: `“${name}” could not be accepted. Please remove it or replace it with a valid supporting file before generating your document.`,
      };
    }

    return {
      blocked: false,
      reason: "",
      totalPages,
      message: "",
    };
  }, [sourceItems]);

  const deriveId = (file) => {
    if (file?.id) return String(file.id);
    const name = file?.name || "unknown";
    const size = file?.size ?? 0;
    const mime = file?.mime || file?.type || "";
    return `${name}::${size}::${mime}`;
  };

  const findMergeId = (file, previousList) => {
    const derived = deriveId(file);
    if (previousList.some((item) => item.id === derived)) return derived;

    if (file?.fileUrl || (Array.isArray(file?.fileUrls) && file.fileUrls.length)) {
      const processingMatch = previousList.find(
        (item) =>
          item.name === file.name &&
          (item.uploading || item.status === "processing")
      );
      if (processingMatch) return processingMatch.id;
    }

    return derived;
  };

  const upsertSources = (incoming = []) => {
    const incomingFiles = Array.isArray(incoming) ? incoming : [];
    if (!incomingFiles.length) return;

    let rejectedForCount = false;

    setSourceItems((previous) => {
      const current = Array.isArray(previous) ? previous : [];
      const map = new Map(current.map((item) => [item.id, item]));

      for (const file of incomingFiles) {
        const id = findMergeId(file, current);
        const alreadyExists = map.has(id);

        if (!alreadyExists && map.size >= DOCWRITER_MAX_SOURCE_FILES) {
          rejectedForCount = true;
          continue;
        }

        const previousItem = map.get(id) || {};

        const incomingUrls = Array.isArray(file.fileUrls)
          ? file.fileUrls
          : file.fileUrl
          ? [file.fileUrl]
          : [];
        const previousUrls = Array.isArray(previousItem.fileUrls)
          ? previousItem.fileUrls
          : previousItem.fileUrl
          ? [previousItem.fileUrl]
          : [];
        const mergedUrls = Array.from(
          new Set([...previousUrls, ...incomingUrls].filter(Boolean))
        );
        const hasFinalUrl = mergedUrls.length > 0;

        const uploadingFlag =
          typeof file.uploading === "boolean"
            ? file.uploading
            : previousItem.uploading ?? false;
        const progressValue =
          typeof file.progress === "number"
            ? Math.max(0, Math.min(100, file.progress))
            : typeof previousItem.progress === "number"
            ? previousItem.progress
            : uploadingFlag
            ? 5
            : 0;

        let statusValue;
        if (file.status) statusValue = file.status;
        else if (hasFinalUrl) statusValue = "ready";
        else if (uploadingFlag) statusValue = "processing";
        else if (progressValue >= 100) statusValue = "ready";
        else statusValue = previousItem.status || "processing";

        const normalized = {
          id,
          name: file.name ?? previousItem.name ?? "Untitled",
          size: typeof file.size === "number" ? file.size : previousItem.size,
          pages: file.pages ?? previousItem.pages,
          pageCount: file.pageCount ?? previousItem.pageCount,
          declaredPages: file.declaredPages ?? previousItem.declaredPages,
          declaredPageCount:
            file.declaredPageCount ?? previousItem.declaredPageCount,
          userPages: file.userPages ?? previousItem.userPages,
          userPageCount: file.userPageCount ?? previousItem.userPageCount,
          detectedPages: file.detectedPages ?? previousItem.detectedPages,
          detectedPageCount:
            file.detectedPageCount ?? previousItem.detectedPageCount,
          pdfPageCount: file.pdfPageCount ?? previousItem.pdfPageCount,
          actualPageCount: file.actualPageCount ?? previousItem.actualPageCount,
          metadata:
            file.metadata && typeof file.metadata === "object"
              ? { ...(previousItem.metadata || {}), ...file.metadata }
              : previousItem.metadata,
          note: file.note ?? previousItem.note,
          status: statusValue,
          uploading: hasFinalUrl ? false : uploadingFlag,
          progress: hasFinalUrl ? 100 : progressValue,
          fileUrl: hasFinalUrl
            ? file.fileUrl || previousItem.fileUrl || mergedUrls[0]
            : file.fileUrl ?? previousItem.fileUrl ?? null,
          fileUrls: mergedUrls,
          previewUrl: file.previewUrl ?? previousItem.previewUrl ?? null,
          rawFile:
            file.rawFile ||
            file.file ||
            file.originalFile ||
            file.sourceFile ||
            previousItem.rawFile ||
            previousItem.file ||
            null,
          type:
            file.type ||
            file.mime ||
            previousItem.type ||
            previousItem.mime ||
            "",
        };

        map.set(id, { ...previousItem, ...normalized });
      }

      return Array.from(map.values()).slice(0, DOCWRITER_MAX_SOURCE_FILES);
    });

    if (rejectedForCount) {
      window.setTimeout(() => {
        setSourceUploadNotice(
          `Doc Writer supports up to ${DOCWRITER_MAX_SOURCE_FILES} supporting files. The additional file was not added.`
        );
      }, 0);
    } else {
      setSourceUploadNotice("");
    }
  };

  const makeBillingNotice = (apiError) => {
    const snap = apiError?.billingSnapshot || {};
    const plan = snap.plan_type || snap.planType;
    const exp = snap.expires_at || snap.expiresAt;
    const remaining = snap.doc_writer_remaining_docs;
    const base = {
      kind: "billing",
      title: "",
      lines: [],
      action: { type: "link", label: "", href: "", external: false },
      iosApp: false,
    };

    const expiredMessage = exp
      ? `Your plan expired on ${new Date(exp).toLocaleDateString()}.`
      : "Your plan is not active.";
    const noCreditsMessage = "You’ve used all your Doc Writer credits.";
    const genericMessage = "No active plan or credits found.";

    if (isIOSApp()) {
      base.iosApp = true;
      base.title = "Action needed";
      base.lines = [
        "To generate a draft, please visit www.asklawly.com and sign in to manage your account.",
        "Then return to the app and tap Generate again.",
      ];
      base.action = {
        type: "link",
        label: "Open www.asklawly.com",
        href: "https://www.asklawly.com",
        external: true,
      };
      return base;
    }

    base.title = "Billing required to generate a draft";
    if (
      String(plan || "").startsWith("monthly") &&
      exp &&
      new Date(exp) < new Date()
    ) {
      base.lines.push(expiredMessage);
    } else if (typeof remaining === "number" && remaining <= 0) {
      base.lines.push(noCreditsMessage);
    } else {
      base.lines.push(genericMessage);
    }

    if (apiError?.reason) base.lines.push(String(apiError.reason));

    base.action = {
      type: "route",
      label: "Open Billing",
      href: "/billing",
      external: false,
    };
    return base;
  };

  const schema = useMemo(
    () => [
      { id: "dueDate", label: "Due Date", type: "date", required: false },
      {
        id: "caseNumber",
        label: "Case or Reference #",
        type: "text",
        help: "Any format accepted (letters, numbers, symbols, spaces).",
      },
      {
        id: "details",
        label: "Key Facts / Details",
        type: "textarea",
        required: true,
        minLength: 10,
        placeholder: "Briefly describe the situation, dates, amounts, names…",
      },
      {
        id: "lengthPreference",
        label: "Length Preference",
        type: "length",
        required: false,
      },
      { id: "urgent", label: "Mark as urgent", type: "checkbox" },
    ],
    []
  );

  const handleLaunchEvent = (event = {}) => {
    const now = Date.now();
    const shouldThrottle =
      event.type === "upload_progress" && Number(event.percent || 0) < 100;

    if (shouldThrottle && now - launchUiThrottleRef.current < 120) return;
    launchUiThrottleRef.current = now;

    setLaunchProgress((previous) => {
      const isNewFile = event.type === "file_start";
      const fileStartedAt =
        event.fileStartedAt ||
        (isNewFile ? new Date().toISOString() : previous.fileStartedAt);

      return {
        ...previous,
        ...event,
        fileNumber: Number(
          event.fileNumber || event.fileIndex || previous.fileNumber || 0
        ),
        totalFiles: Number(
          event.totalFiles || previous.totalFiles || attachmentSummary.count || 0
        ),
        percent: Math.max(
          0,
          Math.min(
            100,
            Number(event.percent ?? (isNewFile ? 0 : previous.percent) ?? 0)
          )
        ),
        loadedBytes: Math.max(
          0,
          Number(event.loadedBytes ?? (isNewFile ? 0 : previous.loadedBytes) ?? 0)
        ),
        totalBytes: Math.max(
          0,
          Number(
            event.totalBytes ??
              event.sizeBytes ??
              previous.totalBytes ??
              previous.sizeBytes ??
              0
          )
        ),
        averageBytesPerSecond: Math.max(
          0,
          Number(
            event.averageBytesPerSecond ??
              (isNewFile ? 0 : previous.averageBytesPerSecond) ??
              0
          )
        ),
        fileStartedAt,
      };
    });

    setLaunchPhase(docWriterLaunchPhaseMessage(event));
  };

  const focusValidationTarget = (targetId, preferredSelectors = []) => {
    window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (!target) return;

      const collapsedToggle = target.querySelector(
        'button[aria-expanded="false"]'
      );
      collapsedToggle?.click?.();

      window.setTimeout(() => {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        const selectors = [
          ...preferredSelectors,
          '[aria-invalid="true"]',
          'input:not([type="hidden"])',
          "select",
          "textarea",
        ];

        const focusable = selectors
          .map((selector) => target.querySelector(selector))
          .find((element) => element && !element.disabled);

        window.setTimeout(() => {
          focusable?.focus?.({ preventScroll: true });
        }, 240);
      }, collapsedToggle ? 90 : 0);
    }, 20);
  };

  const handleGenerate = async () => {
    console.log("[DocWriterStart] Generate clicked");

    if (billingNotice) setBillingNotice(null);

    if (!String(subject || "").trim()) {
      focusValidationTarget("doc-writer-required-subject", [
        'input:not([type="hidden"])',
        '[role="combobox"]',
        "select",
      ]);
      return;
    }

    if (!String(audience || "").trim()) {
      focusValidationTarget("doc-writer-required-audience", [
        '[name*="audience"]',
        '[id*="audience"]',
        '[aria-label*="audience"]',
        "select",
      ]);
      return;
    }

    if (!participantsValid) {
      focusValidationTarget("doc-writer-participants", [
        '[aria-invalid="true"]',
        'input:not([type="hidden"])',
        "select",
      ]);
      return;
    }

    if (!formValid) {
      focusValidationTarget("doc-writer-required-details", [
        '[aria-invalid="true"]',
        "textarea",
        'input[required]',
      ]);
      return;
    }

    if (!isLoggedIn) {
      navigate("/login", { state: { from: "/docwriter/start" } });
      return;
    }

    if (!termsAccepted) {
      focusValidationTarget("doc-writer-required-terms", [
        'input[type="checkbox"]',
      ]);
      return;
    }

    if (billingBlocked) {
      setBillingNotice({
        kind: "billing",
        title: "No Doc Writer document is currently available",
        lines: [
          "No usable Doc Writer allowance is currently available from your monthly plan, trial, or purchased add-ons.",
        ],
        action: onIOSApp
          ? {
              type: "link",
              label: "Open www.asklawly.com",
              href: "https://www.asklawly.com",
              external: true,
            }
          : {
              type: "route",
              label: "Open Billing",
              href: "/billing",
              external: false,
            },
        iosApp: onIOSApp,
      });
      return;
    }

    if (sourcePolicy.blocked) {
      focusValidationTarget("doc-writer-supporting-files");
      return;
    }

    const activeSources = Array.isArray(sourceItems) ? sourceItems : [];

    const missingBrowserFile = activeSources.find(
      (item) =>
        !(
          item?.rawFile ||
          item?.file ||
          item?.originalFile ||
          item?.sourceFile ||
          (typeof File !== "undefined" && item instanceof File)
        )
    );

    if (missingBrowserFile) {
      alert(
        `Please remove and reselect "${missingBrowserFile.name || "the supporting file"}". ` +
          "The new Doc Writer flow uploads the original browser file directly to its reserved job location."
      );
      return;
    }

    try {
      launchProtectionRef.current = true;
      setLaunchProgress({
        type: "idle",
        phase: "idle",
        fileNumber: 0,
        totalFiles: activeSources.length,
        fileName: "",
        sizeBytes: 0,
        loadedBytes: 0,
        totalBytes: 0,
        percent: 0,
        elapsedMs: 0,
        uploadedCount: 0,
        averageBytesPerSecond: 0,
        fileStartedAt: "",
      });
      setLaunchPhase("Creating your Doc Writer workspace…");
      setIsGenerating(true);

      const normalizedParticipants = (Array.isArray(participants)
        ? participants
        : []
      ).map(normalizeParticipantForPayload);

      const recipientParticipant = findParticipantByRole(
        normalizedParticipants,
        "recipient"
      );
      const senderParticipant = findParticipantByRole(
        normalizedParticipants,
        "sender"
      );

      const parsedRecipient = participantToLegacyContact(
        recipientParticipant
      );
      const parsedSender = participantToLegacyContact(senderParticipant);

      if (parsedRecipient.email && !EMAIL_RX.test(parsedRecipient.email)) {
        alert("Recipient email format looks invalid.");
        return;
      }

      if (parsedSender.phone && !PHONE_RX.test(parsedSender.phone)) {
        alert("Sender phone format looks invalid.");
        return;
      }

      const lengthPreferenceValue =
        fieldValues?.lengthPreference || { mode: "auto" };
      const safeCategory = category || "Other / Not Listed";

      const requestPayload = {
        category: safeCategory,
        subject,
        tone,
        audience,
        readingLevel,
        language,
        participants: normalizedParticipants,
        recipient: parsedRecipient,
        sender: parsedSender,
        dueDate: fieldValues.dueDate || null,
        caseNumber: fieldValues.caseNumber || "",
        lengthPreference: lengthPreferenceValue,
        details: fieldValues.details,
        urgent: Boolean(fieldValues.urgent),
        sourceManifest: activeSources.map((item) => ({
          clientId: item.id || deriveId(item),
          filename: item.name || item.rawFile?.name || item.file?.name || "",
          contentType:
            item.type ||
            item.mime ||
            item.rawFile?.type ||
            item.file?.type ||
            "",
          sizeBytes: Number(
            item.size ?? item.rawFile?.size ?? item.file?.size ?? 0
          ),
          declaredPageCount: sourcePageCount(item) || null,
          note: item.note || "",
        })),
        meta: {
          dueDate: fieldValues.dueDate || null,
          caseNumber: fieldValues.caseNumber || "",
          urgent: Boolean(fieldValues.urgent),
          category: safeCategory,
          audience,
        },
      };

      const auth = getAuth();
      const idToken =
        (await currentUser?.getIdToken?.(true)) ||
        (await auth.currentUser?.getIdToken?.(true));

      if (!idToken) {
        navigate("/login", { state: { from: "/docwriter/start" } });
        return;
      }

      /*
       * launchDocwriter performs the authoritative sequence:
       *   1. POST /docwriter/jobs
       *   2. reserve and directly upload every source to signed GCS URLs
       *   3. confirm every exact uploaded object
       *   4. POST /docwriter/jobs/:jobId/generate
       *
       * Generate returns after Cloud Tasks is queued. The browser does not wait
       * for the worker and does not use stream/non-stream draft endpoints.
       */
      const response = await launchDocwriter(
        {
          request: requestPayload,
          sourceItems: activeSources,
          onUploadEvent: handleLaunchEvent,
        },
        idToken
      );

      if (!response?.ok) {
        if (
          response?.error?.status === 402 ||
          response?.error?.status === 403 ||
          response?.error?.code === "BILLING_FORBIDDEN"
        ) {
          setBillingNotice(makeBillingNotice(response.error));
          await fetchBillingSnapshot();
          return;
        }

        alert(
          response?.error?.message ||
            "Doc Writer could not create and queue this document."
        );
        return;
      }

      const data = response.data || {};
      const jobId =
        data.jobId ||
        data.job_id ||
        data.job?.jobId ||
        data.job?.job_id;
      const sessionId =
        data.sessionId ||
        data.session_id ||
        data.job?.sessionId ||
        data.job?.session_id;

      if (!jobId || !sessionId) {
        alert("The document was submitted, but its job identity was not returned.");
        return;
      }

      const uploadedSources = Array.isArray(data.uploads?.uploaded)
        ? data.uploads.uploaded
        : [];

      try {
        sessionStorage.setItem(`docw:launch:${sessionId}`, "1");
        sessionStorage.setItem(`docwriter:job-by-session:${sessionId}`, jobId);
        sessionStorage.setItem(`docwriter:session-by-job:${jobId}`, sessionId);
        sessionStorage.setItem(
          `docwriter:launch:${jobId}`,
          JSON.stringify({
            jobId,
            sessionId,
            request: requestPayload,
            sources: uploadedSources,
            queuedAt: new Date().toISOString(),
          })
        );
      } catch {
        // Navigation state carries the same identifiers.
      }

      setLaunchPhase("Document queued. Opening your editor…");
      launchProtectionRef.current = false;

      navigate(
        `/docwriter/editor?jobId=${encodeURIComponent(
          jobId
        )}&sessionId=${encodeURIComponent(sessionId)}`,
        {
          state: {
            fromStart: true,
            jobId,
            sessionId,
            queued: true,
            initialSources: uploadedSources,
            operationId:
              data.operationId ||
              data.generation?.operationId ||
              data.generation?.operation_id ||
              null,
          },
        }
      );
    } catch (error) {
      console.error("[DocWriterStart] Launch failed:", error);
      alert("An error occurred while preparing your document. Please try again later.");
    } finally {
      launchProtectionRef.current = false;
      setLaunchPhase("");
      setIsGenerating(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate("/login", { state: { from: "/docwriter/start" } });
  };

  const openBilling = () => {
    if (onIOSApp) {
      window.open("https://www.asklawly.com", "_blank", "noopener,noreferrer");
      return;
    }
    navigate("/billing");
  };

  const canUpload =
    isLoggedIn &&
    !isGenerating &&
    sourceItems.length < DOCWRITER_MAX_SOURCE_FILES;

  const barDisabled = isLoggedIn
    ? isGenerating ||
      billingLoading ||
      billingBlocked ||
      !!billingNotice ||
      sourcePolicy.blocked
    : false;

  const parsedFileStart = launchProgress.fileStartedAt
    ? Date.parse(launchProgress.fileStartedAt)
    : 0;
  const liveUploadElapsedMs =
    isGenerating && parsedFileStart
      ? Math.max(
          Number(launchProgress.elapsedMs || 0),
          Math.max(0, launchClockTick - parsedFileStart)
        )
      : Number(launchProgress.elapsedMs || 0);
  const currentFilePercent = Math.max(
    0,
    Math.min(100, Number(launchProgress.percent || 0))
  );
  const currentFileLoadedBytes = Math.max(
    0,
    Number(launchProgress.loadedBytes || 0)
  );
  const currentFileTotalBytes = Math.max(
    0,
    Number(launchProgress.totalBytes || launchProgress.sizeBytes || 0)
  );
  const currentUploadSpeed = Math.max(
    0,
    Number(launchProgress.averageBytesPerSecond || 0)
  );
  const showFileUploadDetails = Boolean(
    launchProgress.fileNumber && launchProgress.totalFiles
  );

  return (
    <div
      className="doc-writer-start-page-root"
      style={{ ...styles.outerWrap, ...(isSmall ? styles.outerWrapMobile : null) }}
    >
      <style>{`
        .doc-writer-start-page-root * { box-sizing: border-box; }
        .doc-writer-start-page-root button,
        .doc-writer-start-page-root input,
        .doc-writer-start-page-root select,
        .doc-writer-start-page-root textarea { font: inherit; }
        .doc-writer-start-page-root a {
          color: #2563EB;
          font-weight: 900;
          text-decoration: none;
        }
        .doc-writer-start-page-root a:hover { text-decoration: underline; }
        @keyframes docWriterLaunchSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes docWriterUploadMove {
          0% { transform: translateX(-100%); background-position: 0 0; }
          100% { transform: translateX(245%); background-position: 200% 0; }
        }
        .doc-writer-start-page-root button:focus-visible,
        .doc-writer-start-page-root input:focus-visible,
        .doc-writer-start-page-root select:focus-visible,
        .doc-writer-start-page-root textarea:focus-visible {
          outline: 3px solid rgba(37,99,235,0.22);
          outline-offset: 2px;
        }
        .doc-writer-modern-fields input:not([type="checkbox"]):not([type="radio"]),
        .doc-writer-modern-fields select,
        .doc-writer-modern-fields textarea {
          width: 100% !important;
          max-width: 100% !important;
          border: 1px solid rgba(15,23,42,0.16) !important;
          border-radius: 12px !important;
          background: #FFFFFF !important;
          color: #0F172A !important;
          padding: 11px 12px !important;
          box-shadow: none !important;
        }
        .doc-writer-modern-fields textarea {
          min-height: 108px;
          resize: vertical;
        }
        .doc-writer-modern-fields textarea.doc-writer-details-voice-textarea {
          padding-right: 64px !important;
          padding-bottom: 58px !important;
        }
        .doc-writer-modern-fields label {
          color: #0F172A !important;
          font-weight: 850 !important;
        }
        .doc-writer-modern-fields button {
          border-radius: 10px !important;
        }
        html, body, #root {
          max-width: 100%;
          overflow-x: hidden;
        }
        .doc-writer-start-page-root,
        .doc-writer-start-page-root *,
        .doc-writer-feature-grid,
        .doc-writer-page-grid,
        .doc-writer-workflow-panel {
          min-width: 0;
          max-width: 100%;
        }
        @media (max-width: 1023px) {
          .doc-writer-start-page-root {
            padding: 14px 12px 34px !important;
          }
        }

        @media (max-width: 640px) {
          .doc-writer-step-header {
            align-items: flex-start !important;
          }

          .doc-writer-step-body {
            padding-top: 13px !important;
          }
        }
      `}</style>

      {isGenerating && launchPhase ? (
        <div
          style={styles.uploadModalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="doc-writer-upload-title"
        >
          <div
            style={{
              ...styles.uploadModalCard,
              ...(isSmall ? styles.uploadModalCardMobile : null),
            }}
          >
            <div style={styles.uploadModalGlow} aria-hidden="true" />
            <div style={styles.uploadModalIconWrap}>
              <span style={styles.uploadModalSpinner} aria-hidden="true" />
            </div>
            <div id="doc-writer-upload-title" style={styles.uploadModalTitle}>
              Preparing your Doc Writer document
            </div>
            <p style={styles.uploadModalText}>
              Please keep this page open. Do not close, refresh, or use the
              browser’s Back button until you are taken to the Doc Writer editor.
            </p>
            <div style={styles.uploadModalStatus} aria-live="polite">
              {launchPhase}
            </div>

            {showFileUploadDetails ? (
              <div style={styles.uploadModalFileCard}>
                <div style={styles.uploadModalFileTop}>
                  <span>
                    File {Number(launchProgress.fileNumber).toLocaleString()} of{" "}
                    {Number(launchProgress.totalFiles).toLocaleString()}
                  </span>
                  <span>{currentFilePercent}%</span>
                </div>
                <div
                  style={styles.uploadModalFileName}
                  title={launchProgress.fileName || ""}
                >
                  {launchProgress.fileName || "Supporting file"}
                </div>
                <div style={styles.uploadModalStats}>
                  <span>
                    {formatBytes(currentFileLoadedBytes)} of{" "}
                    {formatBytes(currentFileTotalBytes)}
                  </span>
                  <span>{formatDuration(liveUploadElapsedMs)}</span>
                  <span>{formatUploadSpeed(currentUploadSpeed)}</span>
                </div>
              </div>
            ) : null}

            <div style={styles.uploadModalTrack}>
              <div
                style={{
                  ...styles.uploadModalFill,
                  ...(showFileUploadDetails
                    ? styles.uploadModalFillDeterminate
                    : null),
                  width: showFileUploadDetails
                    ? `${Math.max(2, currentFilePercent)}%`
                    : "45%",
                }}
              />
            </div>
            <div style={styles.uploadModalFoot}>
              {showFileUploadDetails
                ? "Files upload one at a time. If an upload stops, the dialog will close and your completed form will remain available."
                : "Your secure document workspace is being created. You will be taken to the editor as soon as the request is queued."}
            </div>
          </div>
        </div>
      ) : null}

      {voiceBillingDialogOpen ? (
        <div
          style={styles.voiceBillingOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="doc-writer-voice-billing-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setVoiceBillingDialogOpen(false);
            }
          }}
        >
          <div style={styles.voiceBillingCard}>
            <button
              type="button"
              aria-label="Close billing message"
              title="Close"
              onClick={() => setVoiceBillingDialogOpen(false)}
              style={styles.voiceBillingClose}
            >
              ×
            </button>

            <div style={styles.voiceBillingIcon} aria-hidden="true">
              ◉
            </div>

            <div style={styles.voiceBillingEyebrow}>Doc Writer voice input</div>

            <h2
              id="doc-writer-voice-billing-title"
              style={styles.voiceBillingTitle}
            >
              No Doc Writer document is currently available
            </h2>

            <p style={styles.voiceBillingText}>
              No usable Doc Writer allowance is currently available from your monthly plan, trial, or purchased add-ons.
            </p>

            <div style={styles.voiceBillingActions}>
              <button
                type="button"
                onClick={() => setVoiceBillingDialogOpen(false)}
                style={styles.voiceBillingSecondaryButton}
              >
                Not now
              </button>
              <button
                type="button"
                onClick={() => {
                  setVoiceBillingDialogOpen(false);
                  openBilling();
                }}
                style={styles.voiceBillingPrimaryButton}
              >
                {onIOSApp ? "Manage account" : "Open Billing"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <header style={styles.pageHeader}>
        <div style={styles.pageHeaderText}>
          <div style={{ ...styles.eyebrow, ...(isSmall ? styles.eyebrowMobile : null) }}>
            Doc Writer
          </div>
          <h1 style={{ ...styles.h1, ...(isSmall ? styles.h1Mobile : null) }}>
            Create a professional document
          </h1>
          <p
            style={{
              ...styles.subtitle,
              ...(isSmall ? styles.subtitleMobile : null),
            }}
          >
            Choose from thousands of document subjects, provide the relevant
            facts, and generate an editable draft in minutes.
          </p>
        </div>
      </header>

      <div
        className="doc-writer-feature-grid"
        style={{
          ...styles.featureGrid,
          ...(isSmall ? styles.featureGridMobile : null),
        }}
      >
        <FeatureTile
          icon="▣"
          title="11,000+ Document Subjects"
          text="Choose from 48 categories and thousands of professional subjects."
        />
        <FeatureTile
          icon="✦"
          title="Controlled Writing Preferences"
          text="Set tone, audience, reading level, and language."
        />
        <FeatureTile
          icon="▤"
          title="Editable & Downloadable"
          text="Review, edit, revise, finalize, and download your document."
        />
      </div>

      <div
        className="doc-writer-page-grid"
        style={{
          ...styles.pageGrid,
          ...(isSmall ? styles.pageGridMobile : null),
        }}
      >
        <main style={styles.mainColumn}>
          <section
            className="doc-writer-workflow-panel"
            style={styles.workflowPanel}
          >
            <StepSection
              id="doc-writer-required-subject"
              number="1"
              title="Select or enter a document subject"
              required
            >
              <div className="doc-writer-modern-fields">
                <DocSubjectSelect
                  onSelect={(value) => {
                    if (typeof value === "string") {
                      setSubject(value);
                    } else if (value && typeof value === "object") {
                      setSubject(value?.subject || "");
                    }
                  }}
                  onSelectMeta={({
                    subject: selected,
                    category: selectedCategory,
                  }) => {
                    const safeCategory =
                      selectedCategory === "__ALL__" || !selectedCategory
                        ? "Other / Not Listed"
                        : selectedCategory;
                    setSubject(selected || "");
                    setCategory(safeCategory);
                  }}
                />
              </div>

              <HelperLine>
                Search the complete subject library or enter your own subject in
                the custom-subject field.
              </HelperLine>
            </StepSection>

            <StepSection
              id="doc-writer-required-audience"
              number="2"
              title="Set writing preferences"
              required
            >
              <div
                className="doc-writer-modern-fields"
                style={styles.preferenceStack}
              >
                <DocToneSelect
                  value={tone}
                  onChange={setTone}
                  readingLevel={readingLevel}
                  onReadingLevelChange={setReadingLevel}
                />

                <DocAudienceSelect
                  value={audience}
                  onChange={setAudience}
                />

                <div style={styles.languageBlock}>
                  <label style={styles.fieldLabel}>Language</label>
                  <DocLanguageSelect
                    value={language}
                    onChange={setLanguage}
                    defaultShowExtended={false}
                  />
                </div>
              </div>

              <HelperLine>
                Select an audience before continuing. Tone, reading level, and
                language can be adjusted to match the document.
              </HelperLine>
            </StepSection>

            <StepSection
              id="doc-writer-participants"
              number="3"
              title="Add people or organizations"
              optional
            >
              <div className="doc-writer-modern-fields">
                <DocParticipantsForm
                  category={category}
                  subject={subject}
                  value={participants}
                  onChange={setParticipants}
                  onValidityChange={setParticipantsValid}
                  disabled={isGenerating}
                />
              </div>

              <HelperLine>
                Add only the people, businesses, agencies, courts, or other
                organizations that should be identified in the document.
              </HelperLine>
            </StepSection>

            <StepSection
              id="doc-writer-required-details"
              number="4"
              title="Add document information"
              required
            >
              <div className="doc-writer-modern-fields">
                <DocDynamicFieldsForm
                  schema={schema}
                  values={fieldValues}
                  onChange={setFieldValues}
                  onValidityChange={setFormValid}
                  uid={currentUser?.uid}
                  email={currentUser?.email}
                  userType="individual"
                  sessionId="start-session"
                  voiceAccessAllowed={voiceAccessAllowed}
                  voiceAccessLoading={voiceAccessLoading}
                  onVoiceBlocked={handleVoiceBlocked}
                  voiceSessionKind="fast_chat"
                />
              </div>

              <HelperLine>
                Key Facts / Details is required. Include the important names,
                dates, amounts, deadlines, relationships, and requested outcome.
              </HelperLine>
            </StepSection>

            <StepSection
              id="doc-writer-supporting-files"
              number="5"
              title="Add supporting files"
              optional
            >
              <div className="doc-writer-modern-fields">
                <DocSourcesPanel
                  items={sourceItems}
                  onUploadFiles={upsertSources}
                  onRemove={(id) => {
                    setSourceItems((previous) =>
                      previous.filter((file) => file.id !== id)
                    );
                    setSourceUploadNotice("");
                  }}
                  onPreview={(id) => {
                    const item = sourceItems.find((file) => file.id === id);
                    const url = item?.fileUrl || item?.previewUrl;
                    if (url) window.open(url, "_blank", "noopener");
                  }}
                  uid={currentUser?.uid}
                  email={currentUser?.email}
                  userType="individual"
                  sessionId="start-session"
                  canUpload={canUpload}
                />
              </div>

              {sourcePolicy.blocked ? (
                <Notice type="warning">
                  <strong>Supporting files need attention.</strong>{" "}
                  {sourcePolicy.message}
                </Notice>
              ) : sourceUploadNotice ? (
                <Notice type="warning">
                  <strong>File limit reached.</strong> {sourceUploadNotice}
                </Notice>
              ) : null}

              <HelperLine>
                Add up to {DOCWRITER_MAX_SOURCE_FILES} supporting files, with no
                more than {DOCWRITER_MAX_PAGES_PER_FILE} pages per file and{" "}
                {DOCWRITER_MAX_TOTAL_SOURCE_PAGES} pages total. For files whose
                page count cannot be detected reliably, enter the page count when
                prompted; that declared count is included in the total.
              </HelperLine>
            </StepSection>

            <StepSection
              id="doc-writer-required-terms"
              number="6"
              title="Review and generate"
              required
            >
              <div
                style={{
                  ...styles.reviewGrid,
                  ...(isSmall ? styles.reviewGridMobile : null),
                }}
              >
                <SummaryCard title="Document summary">
                  <SummaryRow
                    label="Subject"
                    value={subject || "Not selected"}
                  />
                  <SummaryRow
                    label="Category"
                    value={category || "Not selected"}
                  />
                  <SummaryRow label="Tone" value={prettyValue(tone)} />
                  <SummaryRow
                    label="Audience"
                    value={prettyValue(audience)}
                  />
                  <SummaryRow
                    label="Language"
                    value={prettyLanguage(language)}
                  />
                  <SummaryRow
                    label="Attachments"
                    value={attachmentSummary.label}
                  />
                </SummaryCard>

                <SummaryCard title="Billing summary">
                  <SummaryRow
                    label="Current billing source"
                    value={
                      billingLoading
                        ? "Checking…"
                        : billingError
                        ? "Server verification"
                        : billingAccess.sourceLabel
                    }
                    accent={billingAccess.canGenerate && !billingError}
                  />
                  <SummaryRow
                    label="Monthly plan remaining"
                    value={
                      billingAccess.unlimited
                        ? "Unlimited"
                        : `${billingAccess.monthlyRemaining} document${
                            billingAccess.monthlyRemaining === 1 ? "" : "s"
                          }`
                    }
                  />
                  <SummaryRow
                    label="Purchased add-ons"
                    value={`${billingAccess.addonRemaining} document${
                      billingAccess.addonRemaining === 1 ? "" : "s"
                    }`}
                  />
                  <SummaryRow
                    label="Available to use now"
                    value={
                      billingAccess.unlimited
                        ? "Unlimited"
                        : Number.isFinite(billingAccess.available)
                        ? `${billingAccess.available} document${
                            billingAccess.available === 1 ? "" : "s"
                          }`
                        : "Unlimited"
                    }
                    accent={billingAccess.canGenerate}
                  />
                </SummaryCard>
              </div>

              {billingError ? (
                <Notice type="warning">
                  {billingError}
                  <button
                    type="button"
                    style={styles.inlineLinkButton}
                    onClick={fetchBillingSnapshot}
                    disabled={billingLoading}
                  >
                    {billingLoading ? "Checking…" : "Refresh access"}
                  </button>
                </Notice>
              ) : null}

              {billingBlocked ? (
                <Notice type="billing">
                  No usable Doc Writer allowance is currently available from your monthly plan, trial, or purchased add-ons.
                </Notice>
              ) : null}

              {!billingBlocked &&
              !sourcePolicy.blocked &&
              String(subject || "").trim() &&
              String(audience || "").trim() &&
              formValid &&
              participantsValid &&
              termsAccepted ? (
                <Notice type="success">
                  You’re all set. Generate the draft, then edit, revise, save, or
                  finalize it from the document workspace.
                </Notice>
              ) : null}

              <InlineNotice notice={billingNotice} navigate={navigate} />

              <label style={styles.termsAgreement}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(event) =>
                    setTermsAccepted(event.target.checked)
                  }
                  disabled={isGenerating}
                  style={styles.termsCheckbox}
                />

                <span style={styles.termsText}>
                  I agree to the{" "}
                  <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Use
                  </a>{" "}
                  and{" "}
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>

              <div style={styles.generateBarWrap}>
                <DocGenerateDraftBar
                  disabled={barDisabled}
                  onGenerate={handleGenerate}
                  isLoading={isGenerating}
                  noteText={
                    sourcePolicy.blocked
                      ? sourcePolicy.message
                      : "Your request will be queued securely and continue in the Doc Writer editor."
                  }
                  buttonLabel="Generate Draft"
                  loadingLabel="Starting…"
                  isLoggedIn={isLoggedIn}
                  onLoginRedirect={handleLoginRedirect}
                />
              </div>
            </StepSection>
          </section>

          <div style={styles.guidanceStack}>
            <GuidanceRow title="Best results">
              Choose the closest subject and provide specific names, dates,
              amounts, deadlines, and requested outcomes.
            </GuidanceRow>
            <GuidanceRow title="AI revisions">
              After generation, you can edit manually or request AI revisions
              from the document workspace. Manual editing remains separate from
              the AI-revision allowance.
            </GuidanceRow>
            <GuidanceRow title="Privacy">
              Supporting files and generated drafts remain associated with your
              account and document session.
            </GuidanceRow>
          </div>

          {onIOSApp ? (
            <p style={styles.iosNote}>
              To manage billing on iOS, visit <strong>www.asklawly.com</strong> in
              your browser.
            </p>
          ) : null}
        </main>

        <aside
          style={{
            ...styles.sideColumn,
            ...(isSmall ? styles.sideColumnMobile : null),
          }}
        >
          <section style={styles.sidePanel}>
            <h3 style={styles.sideTitle}>How Doc Writer works</h3>
            <MiniStep
              number="1"
              title="Choose a document"
              text="Select a library subject or enter your own."
            />
            <MiniStep
              number="2"
              title="Set writing preferences"
              text="Choose the tone, audience, reading level, and language."
            />
            <MiniStep
              number="3"
              title="Add document information"
              text="Provide people, key facts, dates, and optional supporting files."
            />
            <MiniStep
              number="4"
              title="Review and generate"
              text="Review your selections, accept the terms, and generate the draft."
            />
            <MiniStep
              number="5"
              title="Edit, revise, and finalize"
              text="Continue in the editor, then finalize or return later from Doc List."
              last
            />
          </section>

          <section style={styles.sidePanel}>
            <div style={styles.sidePanelHeader}>
              <h3 style={styles.sideTitle}>Your Doc Writer access</h3>
              <button
                type="button"
                style={styles.refreshIconButton}
                onClick={fetchBillingSnapshot}
                disabled={!isLoggedIn || billingLoading}
                aria-label="Refresh Doc Writer access"
              >
                ↻
              </button>
            </div>

            {!isLoggedIn ? (
              <div style={styles.sideMutedText}>
                Log in to view your monthly and purchased Doc Writer access.
              </div>
            ) : billingLoading ? (
              <div style={styles.sideMutedText}>Checking your access…</div>
            ) : billingError ? (
              <div style={styles.sideErrorText}>{billingError}</div>
            ) : (
              <>
                <AccessRow
                  label={billingAccess.planLabel}
                  value={
                    billingAccess.unlimited
                      ? "Unlimited"
                      : billingAccess.monthlyActive
                      ? `${billingAccess.monthlyRemaining} remaining`
                      : billingAccess.trialActive
                      ? `${billingAccess.trialRemaining} remaining`
                      : "Not active"
                  }
                  badge={
                    billingAccess.monthlyActive || billingAccess.trialActive
                      ? "Active"
                      : "Inactive"
                  }
                  active={billingAccess.monthlyActive || billingAccess.trialActive}
                />
                <AccessRow
                  label="Purchased add-ons"
                  value={`${billingAccess.addonRemaining} document${
                    billingAccess.addonRemaining === 1 ? "" : "s"
                  }`}
                />
                <AccessRow
                  label="Current billing source"
                  value={billingAccess.sourceLabel}
                  highlighted
                />

                <div style={styles.accessInfoBox}>
                  {billingAccess.monthlyActive
                    ? "The monthly allowance is used first. If the monthly Doc Writer quota is exhausted, an available purchased add-on can be used next."
                    : "Without an active monthly plan, available purchased add-ons can be used and currently do not expire."}
                </div>
              </>
            )}

            <button
              type="button"
              style={styles.sideButton}
              onClick={openBilling}
            >
              Manage packages <span aria-hidden>›</span>
            </button>
          </section>

        </aside>
      </div>
    </div>
  );
};

function StepSection({
  id,
  number,
  title,
  children,
  required = false,
  optional = false,
  defaultOpen = true,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = `doc-writer-step-${number}-content`;

  return (
    <section id={id} style={styles.stepSection}>
      <div
        className="doc-writer-step-header"
        style={styles.stepHeader}
      >
        <div style={styles.stepHeadingGroup}>
          <div style={styles.stepBadge}>{number}</div>

          <div style={styles.stepTitleBlock}>
            <div style={styles.stepTitleRow}>
              <h2 style={styles.h2}>{title}</h2>

              {required ? (
                <span style={styles.requiredPill}>Required</span>
              ) : optional ? (
                <span style={styles.optionalPill}>Optional</span>
              ) : null}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setOpen((previous) => !previous)}
          aria-expanded={open}
          aria-controls={contentId}
          aria-label={`${open ? "Collapse" : "Expand"} ${title}`}
          style={styles.stepToggleButton}
        >
          <span aria-hidden="true">{open ? "−" : "+"}</span>
        </button>
      </div>

      <div
        id={contentId}
        className="doc-writer-step-body"
        hidden={!open}
        style={{
          ...styles.stepBody,
          ...(!open ? styles.stepBodyHidden : null),
        }}
      >
        {children}
      </div>
    </section>
  );
}

function FeatureTile({ icon, title, text }) {
  return (
    <div style={styles.featureTile}>
      <div style={styles.featureIcon}>{icon}</div>
      <div>
        <h3 style={styles.featureTitle}>{title}</h3>
        <div style={styles.featureText}>{text}</div>
      </div>
    </div>
  );
}

function HelperLine({ children }) {
  return (
    <div style={styles.helperLine}>
      <span style={styles.infoDot}>i</span>
      <span>{children}</span>
    </div>
  );
}

function SummaryCard({ title, children }) {
  return (
    <div style={styles.summaryCard}>
      <h3 style={styles.summaryTitle}>{title}</h3>
      <div style={styles.summaryRows}>{children}</div>
    </div>
  );
}

function SummaryRow({ label, value, accent = false }) {
  return (
    <div style={styles.summaryRow}>
      <span style={styles.summaryLabel}>{label}</span>
      <strong style={{ ...styles.summaryValue, ...(accent ? styles.summaryValueAccent : null) }}>
        {value}
      </strong>
    </div>
  );
}

function Notice({ type = "info", children }) {
  const tone =
    type === "success"
      ? styles.noticeSuccess
      : type === "billing"
      ? styles.noticeBilling
      : type === "warning"
      ? styles.noticeWarning
      : styles.noticeInfo;

  return <div style={{ ...styles.notice, ...tone }}>{children}</div>;
}

function InlineNotice({ notice, navigate }) {
  if (!notice) return null;

  return (
    <div role="status" aria-live="polite" style={{ ...styles.notice, ...styles.noticeBilling }}>
      <div style={styles.noticeTitle}>{notice.title || "Notice"}</div>
      {Array.isArray(notice.lines)
        ? notice.lines.map((line, index) => <div key={index}>{line}</div>)
        : null}
      {notice.action?.type === "route" && !notice.iosApp ? (
        <div style={styles.noticeActions}>
          <button
            type="button"
            onClick={() => navigate(notice.action.href)}
            style={styles.noticeActionButton}
          >
            {notice.action.label}
          </button>
          <a
            href="https://www.asklawly.com/billing"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in browser
          </a>
        </div>
      ) : null}
      {notice.action?.type === "link" && notice.iosApp ? (
        <div style={styles.noticeActions}>
          <a
            href={notice.action.href}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.noticeActionLink}
          >
            {notice.action.label}
          </a>
        </div>
      ) : null}
    </div>
  );
}

function MiniStep({ number, title, text, last = false }) {
  return (
    <div style={styles.miniStep}>
      <div style={styles.miniStepLeft}>
        <div style={styles.miniStepNumber}>{number}</div>
        {!last ? <div style={styles.miniStepLine} /> : null}
      </div>
      <div style={styles.miniStepBody}>
        <div style={styles.miniStepTitle}>{title}</div>
        <div style={styles.miniStepText}>{text}</div>
      </div>
    </div>
  );
}

function AccessRow({ label, value, badge = "", active = false, highlighted = false }) {
  return (
    <div style={{ ...styles.accessRow, ...(highlighted ? styles.accessRowHighlighted : null) }}>
      <div style={styles.accessRowTop}>
        <span style={styles.accessLabel}>{label}</span>
        {badge ? (
          <span style={{ ...styles.accessBadge, ...(active ? styles.accessBadgeActive : null) }}>
            {badge}
          </span>
        ) : null}
      </div>
      <strong style={styles.accessValue}>{value}</strong>
    </div>
  );
}

function GuidanceRow({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.guidanceRow}>
      <button
        type="button"
        style={styles.guidanceButton}
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
      >
        <span style={styles.guidanceIcon}>◌</span>
        <span style={styles.guidanceTitle}>{title}</span>
        <span style={styles.guidanceChevron}>{open ? "−" : "⌄"}</span>
      </button>
      {open ? <div style={styles.guidanceBody}>{children}</div> : null}
    </div>
  );
}

const styles = {
  outerWrap: {
    width: "100%",
    maxWidth: 1120,
    minWidth: 0,
    margin: "0 auto",
    padding: "18px 16px 44px",
    background:
      "radial-gradient(ellipse 82% 68% at 58% 28%, rgba(37,99,235,0.08) 0%, rgba(37,99,235,0.04) 46%, rgba(37,99,235,0) 100%), radial-gradient(ellipse 72% 58% at 36% 72%, rgba(59,130,246,0.045) 0%, rgba(59,130,246,0.022) 48%, rgba(59,130,246,0) 100%), #FFFFFF",
    color: "#0F172A",
    fontFamily: "sans-serif",
    minHeight: "100vh",
    overflowX: "hidden",
    boxSizing: "border-box",
  },
  outerWrapMobile: { padding: "14px 12px 34px" },
  pageHeader: { marginBottom: 18, paddingTop: 12 },
  pageHeaderText: { minWidth: 0 },
  eyebrow: {
    color: "#2563EB",
    fontSize: 13,
    fontWeight: 950,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 7,
  },
  eyebrowMobile: { textAlign: "center" },
  h1: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.12,
    fontWeight: 950,
    letterSpacing: "-0.035em",
  },
  h1Mobile: { fontSize: 28, lineHeight: 1.18, textAlign: "center" },
  subtitle: {
    margin: "8px 0 0",
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.55,
    fontWeight: 650,
    maxWidth: 840,
  },
  subtitleMobile: { textAlign: "center", marginLeft: "auto", marginRight: "auto" },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3,minmax(0,1fr))",
    gap: 14,
    marginBottom: 16,
  },
  featureGridMobile: { gridTemplateColumns: "1fr", gap: 10 },
  featureTile: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minHeight: 82,
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 10px 26px rgba(2,6,23,0.055)",
  },
  featureIcon: {
    width: 44,
    height: 44,
    minWidth: 44,
    borderRadius: 14,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    fontWeight: 950,
  },
  featureTitle: { margin: "0 0 5px", fontSize: 14.5, fontWeight: 950, lineHeight: 1.25 },
  featureText: { color: "#475569", fontSize: 13.5, lineHeight: 1.42, fontWeight: 650 },
  pageGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) 290px",
    gap: 16,
    alignItems: "start",
  },
  pageGridMobile: { gridTemplateColumns: "1fr" },
  mainColumn: { minWidth: 0 },
  workflowPanel: {
    display: "grid",
    gap: 14,
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#F8FAFC",
    boxShadow: "0 14px 34px rgba(2,6,23,0.07)",
    padding: 14,
    overflow: "visible",
  },
  stepSection: {
    minWidth: 0,
    border: "1px solid rgba(2,6,23,0.10)",
    borderRadius: 17,
    background: "#FFFFFF",
    padding: 16,
    boxShadow: "0 7px 20px rgba(2,6,23,0.035)",
  },
  stepHeader: {
    width: "100%",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  stepHeadingGroup: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  stepBadge: {
    width: 38,
    height: 38,
    flex: "0 0 38px",
    borderRadius: 999,
    background: "#2563EB",
    border: "1px solid #2563EB",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    fontWeight: 950,
    boxShadow: "0 6px 14px rgba(37,99,235,0.18)",
  },
  stepTitleBlock: {
    minWidth: 0,
  },
  stepTitleRow: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  h2: {
    minWidth: 0,
    margin: 0,
    fontSize: 20,
    lineHeight: 1.25,
    fontWeight: 950,
    letterSpacing: "-0.3px",
  },
  requiredPill: {
    flex: "0 0 auto",
    border: "1px solid #FECACA",
    borderRadius: 999,
    background: "#FEF2F2",
    color: "#B91C1C",
    padding: "4px 7px",
    fontSize: 9.5,
    lineHeight: 1,
    fontWeight: 950,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  optionalPill: {
    flex: "0 0 auto",
    border: "1px solid rgba(37,99,235,0.16)",
    borderRadius: 999,
    background: "#EFF6FF",
    color: "#1D4ED8",
    padding: "4px 7px",
    fontSize: 9.5,
    lineHeight: 1,
    fontWeight: 950,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  stepToggleButton: {
    width: 38,
    height: 38,
    flex: "0 0 38px",
    border: "1px solid rgba(2,6,23,0.08)",
    borderRadius: 13,
    background: "#F1F5F9",
    color: "#0F172A",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
    fontSize: 21,
    lineHeight: 1,
    fontWeight: 900,
    transition:
      "background 130ms ease, border-color 130ms ease, transform 130ms ease",
  },
  stepBody: {
    width: "100%",
    minWidth: 0,
    paddingTop: 16,
  },
  stepBodyHidden: {
    display: "none",
  },
  helperLine: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 9,
    color: "#64748B",
    fontSize: 13.25,
    lineHeight: 1.45,
    fontWeight: 700,
  },
  infoDot: {
    width: 18,
    height: 18,
    minWidth: 18,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(30,42,120,0.08)",
    border: "1px solid rgba(30,42,120,0.14)",
    color: "#1E2A78",
    fontWeight: 950,
    fontSize: 11,
  },
  preferenceStack: { display: "grid", gap: 16 },
  languageBlock: { display: "grid", gap: 8 },
  fieldLabel: { display: "block", fontWeight: 850, color: "#0F172A" },
  reviewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2,minmax(0,1fr))",
    gap: 12,
  },
  reviewGridMobile: { gridTemplateColumns: "1fr" },
  summaryCard: {
    borderRadius: 15,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#F8FAFC",
    padding: 13,
  },
  summaryTitle: { margin: "0 0 10px", fontSize: 14, fontWeight: 950 },
  summaryRows: { display: "grid", gap: 8 },
  summaryRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    fontSize: 12.5,
    lineHeight: 1.4,
  },
  summaryLabel: { color: "#64748B", fontWeight: 750 },
  summaryValue: { color: "#0F172A", fontWeight: 900, textAlign: "right" },
  summaryValueAccent: {
    color: "#1D4ED8",
    background: "#EFF6FF",
    borderRadius: 999,
    padding: "2px 8px",
  },
  notice: {
    marginTop: 12,
    borderRadius: 13,
    padding: "10px 12px",
    fontSize: 13,
    lineHeight: 1.45,
    fontWeight: 750,
    border: "1px solid",
  },
  noticeTitle: { fontWeight: 950, marginBottom: 5 },
  noticeInfo: { background: "#EFF6FF", color: "#1E40AF", borderColor: "rgba(37,99,235,0.20)" },
  noticeSuccess: { background: "#F0FDF4", color: "#166534", borderColor: "rgba(22,163,74,0.20)" },
  noticeWarning: { background: "#FFFBEB", color: "#92400E", borderColor: "rgba(245,158,11,0.24)" },
  noticeBilling: { background: "#FFF7ED", color: "#9A3412", borderColor: "rgba(234,88,12,0.22)" },
  noticeActions: { marginTop: 9, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
  noticeActionButton: {
    border: "none",
    borderRadius: 10,
    background: "#2563EB",
    color: "#FFFFFF",
    padding: "8px 11px",
    fontWeight: 900,
    cursor: "pointer",
  },
  noticeActionLink: {
    display: "inline-block",
    borderRadius: 10,
    background: "#2563EB",
    color: "#FFFFFF",
    padding: "8px 11px",
    fontWeight: 900,
  },
  inlineLinkButton: {
    marginLeft: 8,
    border: "none",
    background: "transparent",
    color: "#1D4ED8",
    fontWeight: 950,
    cursor: "pointer",
    padding: 0,
  },
  termsAgreement: {
    width: "100%",
    marginTop: 14,
    border: "1px solid rgba(37,99,235,0.16)",
    borderRadius: 13,
    background: "#F8FAFF",
    padding: "12px 13px",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    cursor: "pointer",
  },
  termsCheckbox: {
    width: 18,
    height: 18,
    flex: "0 0 18px",
    margin: "1px 0 0",
    accentColor: "#2563EB",
    cursor: "pointer",
  },
  termsText: {
    color: "#334155",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 750,
  },
  generateBarWrap: { marginTop: 12 },
  uploadModalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 10000,
    display: "grid",
    placeItems: "center",
    padding: 18,
    background: "rgba(2,6,23,0.72)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    overflowY: "auto",
  },
  uploadModalCard: {
    position: "relative",
    width: "min(100%, 520px)",
    minWidth: 0,
    overflow: "hidden",
    borderRadius: 28,
    border: "1px solid rgba(255,255,255,0.70)",
    background: "linear-gradient(180deg,#FFFFFF 0%,#F8FAFF 100%)",
    boxShadow: "0 34px 90px rgba(2,6,23,0.42)",
    padding: "34px 32px 30px",
    textAlign: "center",
  },
  uploadModalCardMobile: {
    borderRadius: 22,
    padding: "28px 20px 24px",
  },
  uploadModalGlow: {
    position: "absolute",
    width: 220,
    height: 220,
    borderRadius: "50%",
    top: -145,
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(37,99,235,0.22)",
    filter: "blur(20px)",
    pointerEvents: "none",
  },
  uploadModalIconWrap: {
    position: "relative",
    width: 66,
    height: 66,
    margin: "0 auto 18px",
    borderRadius: 22,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg,#2563EB,#1D4ED8)",
    boxShadow: "0 16px 32px rgba(37,99,235,0.28)",
  },
  uploadModalSpinner: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "3px solid rgba(255,255,255,0.38)",
    borderTopColor: "#FFFFFF",
    display: "block",
    animation: "docWriterLaunchSpin 0.75s linear infinite",
  },
  uploadModalTitle: {
    position: "relative",
    color: "#0F172A",
    fontWeight: 950,
    fontSize: 22,
    letterSpacing: "-0.02em",
  },
  uploadModalText: {
    position: "relative",
    margin: "10px auto 0",
    maxWidth: 430,
    color: "#475569",
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.65,
  },
  uploadModalStatus: {
    marginTop: 20,
    borderRadius: 15,
    padding: "12px 14px",
    background: "#EFF6FF",
    border: "1px solid rgba(37,99,235,0.15)",
    color: "#1E40AF",
    fontWeight: 900,
    fontSize: 13,
    lineHeight: 1.4,
  },
  uploadModalFileCard: {
    marginTop: 13,
    borderRadius: 15,
    padding: "12px 13px",
    background: "#FFFFFF",
    border: "1px solid rgba(15,23,42,0.09)",
    boxShadow: "0 8px 20px rgba(15,23,42,0.05)",
    textAlign: "left",
    minWidth: 0,
  },
  uploadModalFileTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    color: "#1D4ED8",
    fontSize: 12,
    fontWeight: 950,
  },
  uploadModalFileName: {
    marginTop: 7,
    color: "#0F172A",
    fontSize: 13,
    fontWeight: 900,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  uploadModalStats: {
    marginTop: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "6px 12px",
    color: "#64748B",
    fontSize: 11,
    fontWeight: 800,
  },
  uploadModalTrack: {
    height: 9,
    marginTop: 16,
    borderRadius: 999,
    overflow: "hidden",
    background: "#E2E8F0",
  },
  uploadModalFill: {
    width: "45%",
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg,#2563EB,#60A5FA,#2563EB)",
    backgroundSize: "200% 100%",
    animation: "docWriterUploadMove 1.4s linear infinite",
  },
  uploadModalFillDeterminate: {
    background: "linear-gradient(90deg,#2563EB,#60A5FA)",
    backgroundSize: "100% 100%",
    animation: "none",
    transition: "width 160ms ease",
  },
  uploadModalFoot: {
    marginTop: 13,
    color: "#64748B",
    fontWeight: 700,
    fontSize: 11,
    lineHeight: 1.45,
  },
  voiceBillingOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 10020,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    background: "rgba(15,23,42,0.42)",
    backdropFilter: "blur(5px)",
    WebkitBackdropFilter: "blur(5px)",
  },
  voiceBillingCard: {
    position: "relative",
    width: "min(460px, calc(100vw - 32px))",
    borderRadius: 20,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 28px 70px rgba(15,23,42,0.22)",
    padding: "26px 24px 22px",
    textAlign: "center",
  },
  voiceBillingClose: {
    position: "absolute",
    top: 12,
    right: 12,
    width: 34,
    height: 34,
    borderRadius: 999,
    border: "none",
    background: "#F1F5F9",
    color: "#475569",
    fontSize: 22,
    lineHeight: 1,
    cursor: "pointer",
  },
  voiceBillingIcon: {
    width: 46,
    height: 46,
    margin: "0 auto",
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#EFF6FF",
    color: "#2563EB",
    fontSize: 19,
    fontWeight: 950,
  },
  voiceBillingEyebrow: {
    marginTop: 13,
    color: "#2563EB",
    fontSize: 12,
    fontWeight: 950,
    letterSpacing: "0.035em",
    textTransform: "uppercase",
  },
  voiceBillingTitle: {
    margin: "8px 0 0",
    color: "#0F172A",
    fontSize: 20,
    lineHeight: 1.3,
    fontWeight: 950,
    letterSpacing: "-0.02em",
  },
  voiceBillingText: {
    margin: "11px auto 0",
    maxWidth: 390,
    color: "#475569",
    fontSize: 13.5,
    lineHeight: 1.6,
    fontWeight: 700,
  },
  voiceBillingActions: {
    marginTop: 20,
    display: "flex",
    justifyContent: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  voiceBillingPrimaryButton: {
    minWidth: 130,
    border: "none",
    borderRadius: 12,
    background: "#2563EB",
    color: "#FFFFFF",
    padding: "10px 15px",
    fontWeight: 900,
    cursor: "pointer",
  },
  voiceBillingSecondaryButton: {
    minWidth: 100,
    border: "1px solid rgba(15,23,42,0.12)",
    borderRadius: 12,
    background: "#FFFFFF",
    color: "#334155",
    padding: "10px 15px",
    fontWeight: 850,
    cursor: "pointer",
  },

  sideColumn: { display: "grid", gap: 14, minWidth: 0 },
  sideColumnMobile: { gridRow: "auto" },
  sidePanel: {
    borderRadius: 17,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 12px 28px rgba(2,6,23,0.055)",
    padding: 15,
  },
  sidePanelHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  sideTitle: { margin: 0, fontSize: 15.5, lineHeight: 1.25, fontWeight: 950 },
  sideCopy: { margin: "7px 0 0", color: "#64748B", fontSize: 12.5, lineHeight: 1.5, fontWeight: 700 },
  sideMutedText: { marginTop: 10, color: "#64748B", fontSize: 12.5, lineHeight: 1.5, fontWeight: 700 },
  sideErrorText: { marginTop: 10, color: "#991B1B", fontSize: 12.5, lineHeight: 1.5, fontWeight: 800 },
  refreshIconButton: {
    width: 30,
    height: 30,
    borderRadius: 999,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    color: "#2563EB",
    fontSize: 18,
    fontWeight: 950,
    cursor: "pointer",
  },
  sideButton: {
    appearance: "none",
    border: "1px solid rgba(37,99,235,0.55)",
    background: "#FFFFFF",
    color: "#2563EB",
    borderRadius: 12,
    padding: "10px 12px",
    width: "100%",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 13.5,
  },
  miniStep: { display: "grid", gridTemplateColumns: "30px minmax(0,1fr)", gap: 9, marginTop: 13 },
  miniStepLeft: { display: "flex", flexDirection: "column", alignItems: "center" },
  miniStepNumber: {
    width: 24,
    height: 24,
    borderRadius: 999,
    background: "#2563EB",
    color: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 950,
  },
  miniStepLine: { width: 2, flex: 1, minHeight: 24, marginTop: 4, background: "rgba(37,99,235,0.14)" },
  miniStepBody: { paddingBottom: 4 },
  miniStepTitle: { fontSize: 12.75, fontWeight: 950, lineHeight: 1.35 },
  miniStepText: { marginTop: 3, color: "#64748B", fontSize: 11.75, lineHeight: 1.45, fontWeight: 700 },
  accessRow: { padding: "11px 0", borderBottom: "1px solid rgba(2,6,23,0.08)" },
  accessRowHighlighted: { marginTop: 2, padding: 10, borderRadius: 12, borderBottom: "none", background: "#EFF6FF" },
  accessRowTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  accessLabel: { color: "#334155", fontSize: 12, fontWeight: 850 },
  accessValue: { display: "block", marginTop: 4, color: "#0F172A", fontSize: 13.25, fontWeight: 950 },
  accessBadge: {
    borderRadius: 999,
    padding: "3px 7px",
    background: "#F1F5F9",
    color: "#64748B",
    fontSize: 10,
    fontWeight: 950,
  },
  accessBadgeActive: { background: "#DCFCE7", color: "#166534" },
  accessInfoBox: {
    marginTop: 11,
    borderRadius: 12,
    padding: 10,
    background: "#EFF6FF",
    border: "1px solid rgba(37,99,235,0.16)",
    color: "#1E40AF",
    fontSize: 11.75,
    lineHeight: 1.45,
    fontWeight: 750,
  },
  guidanceStack: { display: "grid", gap: 8, marginTop: 12 },
  guidanceRow: {
    borderRadius: 13,
    border: "1px solid rgba(2,6,23,0.09)",
    background: "#FFFFFF",
    overflow: "hidden",
  },
  guidanceButton: {
    width: "100%",
    border: "none",
    background: "transparent",
    padding: "11px 12px",
    display: "flex",
    alignItems: "center",
    gap: 9,
    cursor: "pointer",
    textAlign: "left",
  },
  guidanceIcon: { color: "#2563EB", fontWeight: 950 },
  guidanceTitle: { flex: 1, color: "#0F172A", fontSize: 12.75, fontWeight: 950 },
  guidanceChevron: { color: "#64748B", fontWeight: 950 },
  guidanceBody: { padding: "0 12px 12px 33px", color: "#64748B", fontSize: 12.25, lineHeight: 1.5, fontWeight: 700 },
  iosNote: { margin: "12px 0 0", textAlign: "center", color: "#64748B", fontSize: 12, fontWeight: 700 },
};

export default DocWriterStartPage;
